import React from "react";
import { motion } from "framer-motion";
import { FiMenu, FiHeadphones, FiArrowRight, FiPlay, FiPieChart, FiMessageCircle, FiHelpCircle, FiCheckSquare, FiThumbsUp, FiShield } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "../../css/PhoneMockup.css";

const PhoneMockup = () => {
    const navigate = useNavigate();

    // Animation Variants
    const containerVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1],
                when: "beforeChildren",
                staggerChildren: 0.15
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    };

    const floatAnimation = {
        y: [0, -10, 0],
        transition: {
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
        }
    };

    return (
        <motion.div
            className="phone_mockup_container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.div className="phone_frame" animate={floatAnimation}>
                <div className="phone_screen">
                    {/* Status Bar */}
                    <div className="pm_status_bar">
                        <div className="pm_notch"></div>
                    </div>

                    {/* Header */}
                    <div className="pm_header">
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <FiMenu className="pm_header_icon" />
                        </motion.div>
                        <div className="pm_logo">
                            <FiShield size={20} />
                            ACTOLOAN
                        </div>
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <FiHeadphones className="pm_header_icon" />
                        </motion.div>
                    </div>

                    {/* Body */}
                    <div className="pm_body">
                        {/* Primary Card: Apply Now */}
                        <motion.div
                            className="pm_card_primary"
                            variants={itemVariants}
                            whileHover={{ y: -4, boxShadow: "0 15px 30px rgba(40, 137, 166, 0.15)" }}
                        >
                            <motion.div
                                className="pm_card_bg_shape"
                                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            ></motion.div>
                            <motion.div
                                className="pm_card_bg_shape_2"
                                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            ></motion.div>

                            <div className="pm_card_content">
                                <div className="pm_card_top_row">
                                    <div className="pm_card_text_group">
                                        <span className="pm_card_subtitle">Pre-approved up to</span>
                                        <h3 className="pm_card_amount">₹1,00,000</h3>
                                    </div>
                                    <motion.div
                                        className="pm_progress_circle"
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                                    >
                                        <motion.div
                                            className="pm_progress_inner"
                                            animate={{ rotate: -360 }}
                                            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                                        >
                                            <span style={{ fontSize: '14px', color: '#0f172a' }}>98</span><span style={{ fontSize: '9px', color: '#10b981' }}>%</span>
                                        </motion.div>
                                    </motion.div>
                                </div>

                                <motion.div
                                    className="pm_apply_btn_new"
                                    whileHover={{ scale: 1.02, boxShadow: "0 10px 25px rgba(40, 137, 166, 0.4)" }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => navigate('/apply-now')}
                                >
                                    Get Funds Now <FiArrowRight size={16} />
                                </motion.div>
                            </div>
                        </motion.div>

                        {/* Secondary Card: Quick Apply CTA */}
                        <motion.div
                            className="pm_card_secondary pm_pulse_glow"
                            variants={itemVariants}
                            whileHover={{ y: -4, scale: 1.02, boxShadow: "0 15px 30px rgba(16, 185, 129, 0.4)" }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => navigate('/apply-now')}
                            style={{ cursor: "pointer" }}
                        >
                            <div className="pm_card_secondary_content">
                                <h4 style={{ fontSize: '15px', whiteSpace: 'nowrap' }}>Apply For Loan Now</h4>
                                <p style={{ fontSize: '11px', opacity: 0.9, whiteSpace: 'nowrap' }}>Takes only 10 minutes to complete</p>
                            </div>
                            <motion.div
                                className="pm_play_btn pm_arrow_pulse"
                                style={{ marginLeft: 'auto', background: '#ffffff', color: '#10b981' }}
                            >
                                <FiArrowRight size={18} />
                            </motion.div>
                        </motion.div>

                        {/* Quick Links Grid */}
                        <motion.h3 className="pm_quick_links_title" variants={itemVariants}>Quick Links</motion.h3>
                        <div className="pm_grid">
                            {[
                                { icon: <FiPieChart />, text: "Loan Calculator" },
                                { icon: <FiCheckSquare />, text: "Check Credit Score" },
                                { icon: <FiHelpCircle />, text: "How to Apply" },
                                { icon: <FiMessageCircle />, text: "Reach Us For Help" }
                            ].map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    className="pm_grid_item"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.03, borderColor: "#38bdf8", boxShadow: "0 5px 15px rgba(56,189,248,0.15)" }}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    <div className="pm_grid_icon">{item.icon}</div>
                                    <span>{item.text}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Bottom Navigation Footer */}
                    <div className="pm_nav_footer">
                        <div className="pm_nav_item active">
                            <FiMenu size={20} />
                            <span>Home</span>
                        </div>
                        <div className="pm_nav_item">
                            <FiPieChart size={20} />
                            <span>Loans</span>
                        </div>
                        <div className="pm_nav_item">
                            <FiMessageCircle size={20} />
                            <span>Inbox</span>
                        </div>
                        <div className="pm_nav_item">
                            <FiHelpCircle size={20} />
                            <span>Profile</span>
                        </div>
                    </div>
                </div> {/* End phone_screen */}
            </motion.div>

            {/* Overlapping Congratulations Pop-up */}
            <motion.div
                className="pm_popup_card"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15, delay: 1.2 }}
            >
                <motion.div
                    className="pm_popup_icon"
                    animate={{ scale: [1, 1.2, 1], rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                    <FiThumbsUp />
                </motion.div>
                <h4>Congratulations!</h4>
                <p>You are eligible for the loan</p>
            </motion.div>

        </motion.div>
    );
};

export default PhoneMockup;
